import { UpdateActualityInput } from '@warebee/frontend/data-access-api-graphql';
import {
  DatasetExtraField,
  DatasetExtraFieldsSettings,
  FilterPresets,
} from '@warebee/shared/common';
import {
  ActualityAggregatedHqDataRow,
  ActualityHqDataColumn,
} from '@warebee/shared/dataset-queries';
import { TFunction } from 'i18next';
import { HeatmapMetricDescriptor } from '../../common/heatmap.types';
import { HelpIndex, HelpNavTo } from '../../helpContext/help.default';
import { ActualityMetricDataRow } from '../../metrics/actuality/actualityMetric.types';
import { ActualityAssignmentMetricDataRow } from '../../metrics/actualityAssignment/actualityAssignmentMetric.types';
import { FilterSettings } from '../../policyFilters/policyFilter.types';
import {
  getMetricGroupsConfig,
  type MetricGroupId,
} from './metricGroups.config';

export const ActualityActivityDatasetTypeKey = 'actuality-activity-feed';
export const ActualityLayoutDatasetTypeKey = 'actuality-layout';
export const ActualityAssignmentDatasetTypeKey = 'actuality-assignment';
export const ActualityItemSetDatasetTypeKey = 'actuality-item-set';
export type ActualityDatasetTypeKey =
  | typeof ActualityActivityDatasetTypeKey
  | typeof ActualityLayoutDatasetTypeKey
  | typeof ActualityAssignmentDatasetTypeKey
  | typeof ActualityItemSetDatasetTypeKey;

export const actualityAutoAnalyzeStatus = [
  'none',
  'progress',
  'done',
  'error',
] as const;
export type ActualityAutoAnalyzeStatus =
  (typeof actualityAutoAnalyzeStatus)[number];

export const actualityMainViewTypes = [
  'layout',
  'timeline',
  'combined',
] as const;
export type ActualityMainViewType = (typeof actualityMainViewTypes)[number];

export const actualityHeatmapTypes = ['events', 'assignment'] as const;
export type ActualityHeatmapType = (typeof actualityHeatmapTypes)[number];

export type ActualityDatasetPatch = Pick<
  UpdateActualityInput,
  | 'activityFeedId'
  | 'assignmentId'
  | 'layoutId'
  | 'itemSetId'
  | 'orderSetId'
  | 'inventorySnapshotId'
>;

export const timelineFilterByPerformance = [
  'all',
  'underperform',
  'underperform20',
] as const;

export type TimelineFilterByPerformance =
  (typeof timelineFilterByPerformance)[number];

export const timelineFilterByType = [
  'user',
  'proccess',
  'performance',
] as const;

export type TimelineFilterByType = (typeof timelineFilterByType)[number];

export type ActualityExtraMetricDescriptors = {
  assignmentMetrics: {
    descriptor: HeatmapMetricDescriptor<
      string,
      ActualityAssignmentMetricDataRow
    >[];
  };
};

export const ACTUALITY_CONGESTION_THRESHOLD_SEC_DEFAULT = 120;
export const ACTUALITY_CONGESTION_DISTANCE_DEFAULT = 60;

export type ActualityExtraSettings = {
  autoRefresh: boolean;
  refreshInterval: number;
  congestionIntervalMinutes?: number;
  congestionDistance?: number;
  congestionThresholdSeconds?: number;
  assignmentExtraFields: DatasetExtraField[];
  activityExtraFields: DatasetExtraField[];
  assignmentMetricDescriptors: HeatmapMetricDescriptor<
    string,
    ActualityAssignmentMetricDataRow
  >[];
  actualityMetricDescriptors: HeatmapMetricDescriptor<
    string,
    ActualityMetricDataRow
  >[];

  filterPreset?: FilterPresets<ActualityHqDataColumn>;
  filterSettings?: FilterSettings[];
  datasetExtraFieldsSettings?: DatasetExtraFieldsSettings<ActualityDatasetTypeKey>[];
};

export type ActualityEntityType =
  | 'event'
  | 'location'
  | 'item'
  | 'job'
  | 'agentId'
  | 'order'
  | 'shift'
  | 'inventory';

export type ActualityDataSetTableType =
  | 'layout'
  | 'assignment'
  | 'inventory'
  | 'item-set'
  | 'order-set'
  | 'activity-feed'
  | 'jobs'
  | 'activity-metric'
  | 'activity-plan'
  | 'congested-events'
  | 'kpi-dashboard'
  | 'kpi-metrics';

export interface MetricGroupConfig {
  id: string;
  order: number;
  title: string;
  description: string;
  visible?: boolean;
  helpNavTo?: HelpNavTo<HelpIndex>;
}

export interface MetricDescriptor {
  type: string;
  title: string;
  titleCategory?: string;
  description?: string;
}

export interface KpiMetricRow {
  metricId: string;
  title: string;
  legend?: string;
  category: string;
  description: string;
  rawValue: number;
  formattedValue: string;
  unit: string;
  hasData: boolean;
  chartTypes: string[];
  color?: [string, string];
}

export interface KpiGroupRow {
  groupId: string;
  groupValue: string;
  groupLabel: string;
  [metricId: string]: any; // Dynamic metric values
}

export type { MetricGroupId };

export const createMetricGroupConfig = (
  t: TFunction,
  id: MetricGroupId,
  defaultVisible = true,
): MetricGroupConfig => {
  const config = getMetricGroupsConfig(t)[id];
  return {
    ...config,
    visible: defaultVisible,
  };
};

export type ActualityJob = Pick<
  ActualityAggregatedHqDataRow,
  'jobId' | 'agentId' | 'agentType' | 'eventProcessType'
> &
  ActualityAggregatedHqDataRow;

export type ActualityJobs = ActualityJob[];
export type ActualityJobField = keyof ActualityJob;
